import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import apiClient, { Annotation } from '../api/client';
import AnnotationToolbar, { AnnotationTool } from '../components/evidence/AnnotationToolbar';
import PDFViewer from '../components/evidence/PDFViewer';
import ImageViewer from '../components/evidence/ImageViewer';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Maximize,
  Minimize,
  Download,
  FileText,
  Film,
  Image,
  Music,
  ChevronLeft,
  ChevronRight,
  Check,
  List,
  Pencil,
  Volume2,
  VolumeX,
  Send,
  Inbox,
  X,
  CheckCircle,
  XCircle,
  Bell,
} from 'lucide-react';

interface Exhibit {
  id: string;
  caseNumber: string;
  exhibitNumber: string;
  title: string;
  filingStatus: string;
  evidenceItems?: EvidenceItem[];
}

interface EvidenceItem {
  id: string;
  evidenceId: string;
  displayOrder: number;
  notes?: string;
  evidenceTitle?: string;
  fileName?: string;
  evidenceType?: string;
  url?: string;
}

interface CourtShare {
  Id: string;
  CaseNumber: string;
  ExhibitId?: string;
  EvidenceId?: string;
  SharedByName: string;
  SharedByEmail: string;
  SharedByRole: string;
  SharedWithName: string;
  SharedWithEmail: string;
  SharedWithRole: string;
  Message?: string;
  Status: 'Pending' | 'Acknowledged' | 'Dismissed';
  SharedAt: string;
  AcknowledgedAt?: string;
  ExhibitNumber?: string;
  ExhibitTitle?: string;
  EvidenceTitle?: string;
}

interface CaseParty {
  Email: string;
  Name: string;
  Role: string;
  label: string;
}

export default function Courtroom() {
  const { user } = useAuth();
  const [selectedCase, setSelectedCase] = useState<string>('');
  const [selectedExhibit, setSelectedExhibit] = useState<Exhibit | null>(null);
  const [currentEvidenceIndex, setCurrentEvidenceIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [activeTool, setActiveTool] = useState<AnnotationTool>(null);
  const [activeColor, setActiveColor] = useState('#EF4444');
  const [rectangleFill, setRectangleFill] = useState(false);
  const canRedact = user?.role === 'Attorney' || user?.role === 'CaseManager' || user?.role === 'SystemAdmin';
  
  // Court sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReceivedPanel, setShowReceivedPanel] = useState(false);
  const [selectedShareRecipient, setSelectedShareRecipient] = useState<string>('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareType, setShareType] = useState<'exhibit' | 'evidence'>('exhibit');
  
  const presentationRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Get all cases for selection
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Get filed exhibits for the selected case
  const { data: exhibits } = useQuery({
    queryKey: ['exhibits', selectedCase, 'filed'],
    queryFn: async () => {
      if (!selectedCase) return [];
      const allExhibits = await apiClient.getExhibits(selectedCase);
      // Only show Filed or Acknowledged exhibits (admitted evidence)
      return allExhibits.filter((e: Exhibit) => 
        e.filingStatus === 'Filed' || e.filingStatus === 'Acknowledged'
      );
    },
    enabled: !!selectedCase,
  });

  // Get exhibit details with evidence items
  const { data: exhibitDetails } = useQuery({
    queryKey: ['exhibit', selectedExhibit?.id],
    queryFn: () => selectedExhibit ? apiClient.getExhibit(selectedExhibit.id) : null,
    enabled: !!selectedExhibit,
  });

  // Get case parties for sharing
  const { data: caseParties } = useQuery({
    queryKey: ['case-parties', selectedCase],
    queryFn: () => apiClient.getCaseParties(selectedCase),
    enabled: !!selectedCase,
  });

  // Get received court shares
  const { data: receivedShares, refetch: refetchShares } = useQuery({
    queryKey: ['court-shares', selectedCase],
    queryFn: () => apiClient.getCourtShares(selectedCase),
    enabled: !!selectedCase,
    refetchInterval: 5000, // Poll every 5 seconds for updates
  });

  // Filter shares: pending ones for current user
  const pendingShares = receivedShares?.filter(
    (s: CourtShare) => s.Status === 'Pending' && s.SharedWithEmail === user?.email
  ) || [];

  // Create share mutation
  const createShareMutation = useMutation({
    mutationFn: (data: { caseNumber: string; exhibitId?: string; evidenceId?: string; sharedWithEmail: string; message?: string }) =>
      apiClient.createCourtShare(data),
    onSuccess: () => {
      setShowShareModal(false);
      setSelectedShareRecipient('');
      setShareMessage('');
      refetchShares();
    },
  });

  // Acknowledge share mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (shareId: string) => apiClient.acknowledgeCourtShare(shareId),
    onSuccess: () => refetchShares(),
  });

  // Dismiss share mutation
  const dismissMutation = useMutation({
    mutationFn: (shareId: string) => apiClient.dismissCourtShare(shareId),
    onSuccess: () => refetchShares(),
  });

  // Real-time updates handled by polling (refetchInterval: 5000)

  const currentEvidence = exhibitDetails?.evidenceItems?.[currentEvidenceIndex];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedExhibit) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          prevEvidence();
          break;
        case 'ArrowRight':
          nextEvidence();
          break;
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'm':
        case 'M':
          setIsMuted(!isMuted);
          break;
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen();
          }
          break;
        case 's':
        case 'S':
          setShowSidebar(!showSidebar);
          break;
        case 'a':
        case 'A':
          setShowAnnotation(!showAnnotation);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedExhibit, currentEvidenceIndex, isFullscreen, isMuted, showSidebar, showAnnotation]);

  // Fullscreen handlers
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      presentationRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  const exitFullscreen = useCallback(() => {
    document.exitFullscreen();
    setIsFullscreen(false);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Navigation
  const nextEvidence = useCallback(() => {
    if (exhibitDetails?.evidenceItems && currentEvidenceIndex < exhibitDetails.evidenceItems.length - 1) {
      setCurrentEvidenceIndex(i => i + 1);
    }
  }, [exhibitDetails, currentEvidenceIndex]);

  const prevEvidence = useCallback(() => {
    if (currentEvidenceIndex > 0) {
      setCurrentEvidenceIndex(i => i - 1);
    }
  }, [currentEvidenceIndex]);

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Load annotations when evidence changes
  useEffect(() => {
    if (currentEvidence?.evidenceId) {
      apiClient.getAnnotations(currentEvidence.evidenceId)
        .then(setAnnotations)
        .catch(() => setAnnotations([]));
    } else {
      setAnnotations([]);
    }
  }, [currentEvidence?.evidenceId]);

  const handleAnnotationCreate = useCallback(async (annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
  }) => {
    if (!currentEvidence?.evidenceId) return;
    try {
      const created = await apiClient.createAnnotation(currentEvidence.evidenceId, {
        ...annotation,
        visibility: 'courtroom',
      });
      setAnnotations(prev => [...prev, created]);
    } catch (err) {
      console.error('Failed to create annotation:', err);
    }
  }, [currentEvidence?.evidenceId]);

  const handleAnnotationDelete = useCallback(async (annotationId: string) => {
    if (!currentEvidence?.evidenceId) return;
    try {
      await apiClient.deleteAnnotation(currentEvidence.evidenceId, annotationId);
      setAnnotations(prev => prev.filter(a => a.Id !== annotationId));
    } catch (err) {
      console.error('Failed to delete annotation:', err);
    }
  }, [currentEvidence?.evidenceId]);

  // Download offline package
  const downloadOfflinePackage = async () => {
    if (!exhibitDetails) return;
    
    // In a real app, this would call an API to generate a ZIP
    alert(`Downloading offline package for ${exhibitDetails.exhibitNumber}:\n\n` +
      `Contains ${exhibitDetails.evidenceItems?.length || 0} evidence items\n` +
      `Case: ${exhibitDetails.caseNumber}`);
  };

  // Handle share submission
  const handleShareSubmit = () => {
    if (!selectedCase || !selectedShareRecipient) return;
    
    createShareMutation.mutate({
      caseNumber: selectedCase,
      exhibitId: shareType === 'exhibit' ? selectedExhibit?.id : undefined,
      evidenceId: shareType === 'evidence' ? currentEvidence?.evidenceId : undefined,
      sharedWithEmail: selectedShareRecipient,
      message: shareMessage || undefined,
    });
  };

  // View shared exhibit/evidence
  const viewSharedItem = (share: CourtShare) => {
    if (share.ExhibitId) {
      const exhibit = exhibits?.find((e: Exhibit) => e.id === share.ExhibitId);
      if (exhibit) {
        setSelectedExhibit(exhibit);
        setCurrentEvidenceIndex(0);
      }
    }
    acknowledgeMutation.mutate(share.Id);
    setShowReceivedPanel(false);
  };

  // Get evidence type icon
  const getEvidenceIcon = (type?: string) => {
    switch (type?.toLowerCase()) {
      case 'video':
        return <Film className="w-5 h-5" />;
      case 'audio':
        return <Music className="w-5 h-5" />;
      case 'image':
        return <Image className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  // Render evidence content
  const renderEvidence = () => {
    if (!currentEvidence) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <p>Select an exhibit to begin presentation</p>
        </div>
      );
    }

    const evidenceType = currentEvidence.evidenceType?.toLowerCase();
    // Use URL from API, fallback to stream endpoint
    const evidenceUrl = currentEvidence.url || `/api/evidence/${currentEvidence.evidenceId}/stream`;

    if (evidenceType === 'video') {
      return (
        <div className="relative w-full h-full flex items-center justify-center bg-black">
          <video
            ref={videoRef}
            className="max-w-full max-h-full"
            src={evidenceUrl}
            muted={isMuted}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            controls={false}
          >
            <track kind="captions" />
          </video>
        </div>
      );
    }

    if (evidenceType === 'image') {
      return (
        <div className="relative w-full h-full">
          <ImageViewer
            url={evidenceUrl}
            alt={currentEvidence.evidenceTitle || 'Evidence'}
            annotations={annotations}
            onAnnotationCreate={handleAnnotationCreate}
            onAnnotationDelete={handleAnnotationDelete}
            activeTool={showAnnotation ? activeTool : null}
            activeColor={activeColor}
            rectangleFill={rectangleFill}
            readOnly={!showAnnotation}
          />
        </div>
      );
    }

    if (evidenceType === 'audio') {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-900 text-white">
          <Music className="w-24 h-24 mb-4" />
          <p className="text-xl">{currentEvidence.evidenceTitle || currentEvidence.fileName}</p>
          <audio
            ref={videoRef as any}
            src={evidenceUrl}
            className="mt-4"
            controls
            muted={isMuted}
          />
        </div>
      );
    }

    // Document/PDF
    return (
      <div className="w-full h-full">
        <PDFViewer
          url={evidenceUrl}
          annotations={annotations}
          onAnnotationCreate={handleAnnotationCreate}
          onAnnotationDelete={handleAnnotationDelete}
          activeTool={showAnnotation ? activeTool : null}
          activeColor={activeColor}
          rectangleFill={rectangleFill}
          readOnly={!showAnnotation}
        />
      </div>
    );
  };

  // Role check
  if (!user || !['Attorney', 'CaseManager', 'SystemAdmin', 'Judge'].includes(user.role)) {
    return (
      <div className="p-8 text-center text-gray-500">
        You don't have permission to access Courtroom Presentation.
      </div>
    );
  }

  return (
    <div className="fixed inset-0 lg:left-64 top-14 z-30 flex flex-col bg-gray-900">
      {/* Header - Hidden in fullscreen */}
      {!isFullscreen && (
        <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold">Courtroom Presentation</h1>
            
            {/* Case selector */}
            <select
              value={selectedCase}
              onChange={(e) => {
                setSelectedCase(e.target.value);
                setSelectedExhibit(null);
                setCurrentEvidenceIndex(0);
              }}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm"
            >
              <option value="">Select Case...</option>
              {cases?.map((c: { caseNumber: string; title: string }) => (
                <option key={c.caseNumber} value={c.caseNumber}>
                  {c.caseNumber} - {c.title}
                </option>
              ))}
            </select>

            {/* Exhibit selector */}
            {selectedCase && (
              <select
                value={selectedExhibit?.id || ''}
                onChange={(e) => {
                  const exhibit = exhibits?.find((ex: Exhibit) => ex.id === e.target.value);
                  setSelectedExhibit(exhibit || null);
                  setCurrentEvidenceIndex(0);
                }}
                className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm"
              >
                <option value="">Select Exhibit...</option>
                {exhibits?.map((exhibit: Exhibit) => (
                  <option key={exhibit.id} value={exhibit.id}>
                    {exhibit.exhibitNumber} - {exhibit.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              Keyboard: ←/→ navigate | Space play/pause | F fullscreen | A annotate | S sidebar
            </span>
            
            {/* Received Items Button */}
            {pendingShares.length > 0 && (
              <button
                onClick={() => setShowReceivedPanel(!showReceivedPanel)}
                className="relative flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium"
              >
                <Inbox className="w-4 h-4" />
                <span>{pendingShares.length} Received</span>
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div ref={presentationRef} className="flex-1 flex overflow-hidden">
        {/* Sidebar - Exhibit queue */}
        {showSidebar && !isFullscreen && (
          <div className="w-64 bg-gray-800 text-white overflow-y-auto">
            <div className="p-3 border-b border-gray-700 flex items-center justify-between">
              <h2 className="font-medium flex items-center gap-2">
                <List className="w-4 h-4" />
                Evidence Queue
              </h2>
              <span className="text-xs text-gray-400">
                {exhibitDetails?.evidenceItems?.length || 0} items
              </span>
            </div>
            
            {exhibitDetails?.evidenceItems?.map((item: EvidenceItem, index: number) => (
              <button
                key={item.id}
                onClick={() => setCurrentEvidenceIndex(index)}
                className={`w-full p-3 text-left border-b border-gray-700 hover:bg-gray-700 transition-colors ${
                  index === currentEvidenceIndex ? 'bg-blue-600' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {getEvidenceIcon(item.evidenceType)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {item.evidenceTitle || item.fileName || `Item ${index + 1}`}
                    </p>
                    <p className="text-xs text-gray-400">{item.evidenceType}</p>
                  </div>
                  {index === currentEvidenceIndex && (
                    <Check className="w-4 h-4 text-green-400" />
                  )}
                </div>
              </button>
            ))}

            {(!exhibitDetails?.evidenceItems || exhibitDetails.evidenceItems.length === 0) && (
              <div className="p-4 text-center text-gray-500 text-sm">
                No evidence items in this exhibit
              </div>
            )}
          </div>
        )}

        {/* Main presentation area */}
        <div className="flex-1 flex flex-col">
          {/* Evidence display */}
          <div className="flex-1 relative">
            {renderEvidence()}

            {/* Annotation toolbar overlay */}
            {showAnnotation && (
              <div className="absolute top-4 right-4 z-10">
                <AnnotationToolbar
                  activeTool={activeTool}
                  onToolChange={setActiveTool}
                  activeColor={activeColor}
                  onColorChange={setActiveColor}
                  rectangleFill={rectangleFill}
                  onRectangleFillChange={setRectangleFill}
                  annotationCount={annotations.length}
                  canRedact={canRedact}
                />
              </div>
            )}

            {/* Annotation count indicator */}
            {annotations.length > 0 && !showAnnotation && (
              <div className="absolute top-4 left-4 bg-gray-800/80 text-white px-3 py-1 rounded text-sm">
                {annotations.length} annotation{annotations.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Controls bar */}
          <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
            {/* Left - Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={prevEvidence}
                disabled={currentEvidenceIndex === 0}
                className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous (←)"
              >
                <SkipBack className="w-5 h-5" />
              </button>
              
              <button
                onClick={togglePlayPause}
                className="p-2 rounded hover:bg-gray-700"
                title="Play/Pause (Space)"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              
              <button
                onClick={nextEvidence}
                disabled={!exhibitDetails?.evidenceItems || currentEvidenceIndex >= exhibitDetails.evidenceItems.length - 1}
                className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next (→)"
              >
                <SkipForward className="w-5 h-5" />
              </button>

              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 rounded hover:bg-gray-700"
                title="Mute/Unmute (M)"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Center - Current exhibit info */}
            <div className="text-center">
              {selectedExhibit && (
                <>
                  <div className="text-sm font-medium">
                    {selectedExhibit.exhibitNumber} - {selectedExhibit.title}
                  </div>
                  <div className="text-xs text-gray-400">
                    Item {currentEvidenceIndex + 1} of {exhibitDetails?.evidenceItems?.length || 0}
                  </div>
                </>
              )}
            </div>

            {/* Right - Tools */}
            <div className="flex items-center gap-2">
              {/* Share Button */}
              <button
                onClick={() => setShowShareModal(true)}
                disabled={!selectedExhibit && !currentEvidence}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-sm font-medium"
                title="Share with Parties"
              >
                <Send className="w-4 h-4" />
                Share
              </button>

              <div className="w-px h-6 bg-gray-600" />

              <button
                onClick={() => setShowAnnotation(!showAnnotation)}
                className={`p-2 rounded hover:bg-gray-700 ${showAnnotation ? 'bg-red-600' : ''}`}
                title="Annotation Mode (A)"
              >
                <Pencil className="w-5 h-5" />
              </button>

              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className={`p-2 rounded hover:bg-gray-700 ${showSidebar ? 'bg-blue-600' : ''}`}
                title="Toggle Sidebar (S)"
              >
                {showSidebar ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>

              <button
                onClick={downloadOfflinePackage}
                className="p-2 rounded hover:bg-gray-700"
                title="Download Offline Package"
              >
                <Download className="w-5 h-5" />
              </button>

              <button
                onClick={toggleFullscreen}
                className="p-2 rounded hover:bg-gray-700"
                title="Fullscreen (F)"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen overlay controls */}
      {isFullscreen && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={prevEvidence}
              disabled={currentEvidenceIndex === 0}
              className="p-3 rounded-full bg-white/20 hover:bg-white/30 disabled:opacity-50"
            >
              <SkipBack className="w-6 h-6 text-white" />
            </button>
            
            <button
              onClick={togglePlayPause}
              className="p-4 rounded-full bg-white/20 hover:bg-white/30"
            >
              {isPlaying ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white" />}
            </button>
            
            <button
              onClick={nextEvidence}
              disabled={!exhibitDetails?.evidenceItems || currentEvidenceIndex >= exhibitDetails.evidenceItems.length - 1}
              className="p-3 rounded-full bg-white/20 hover:bg-white/30 disabled:opacity-50"
            >
              <SkipForward className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="text-white text-center">
            <div className="text-lg font-medium">{selectedExhibit?.exhibitNumber}</div>
            <div className="text-sm opacity-75">
              {currentEvidenceIndex + 1} / {exhibitDetails?.evidenceItems?.length || 0}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAnnotation(!showAnnotation)}
              className={`p-3 rounded-full ${showAnnotation ? 'bg-red-600' : 'bg-white/20 hover:bg-white/30'}`}
            >
              <Pencil className="w-6 h-6 text-white" />
            </button>
            
            <button
              onClick={exitFullscreen}
              className="p-3 rounded-full bg-white/20 hover:bg-white/30"
            >
              <Minimize className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Send className="w-5 h-5" />
                Share with Party
              </h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Share Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Share Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShareType('exhibit')}
                    className={`flex-1 py-2 px-3 rounded ${shareType === 'exhibit' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  >
                    Entire Exhibit
                  </button>
                  <button
                    onClick={() => setShareType('evidence')}
                    disabled={!currentEvidence}
                    className={`flex-1 py-2 px-3 rounded ${shareType === 'evidence' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'} disabled:opacity-50`}
                  >
                    Current Item
                  </button>
                </div>
              </div>

              {/* What's being shared */}
              <div className="bg-gray-900 rounded p-3">
                <p className="text-sm text-gray-400 mb-1">Sharing:</p>
                {shareType === 'exhibit' ? (
                  <p className="font-medium">{selectedExhibit?.exhibitNumber} - {selectedExhibit?.title}</p>
                ) : (
                  <p className="font-medium">{currentEvidence?.evidenceTitle || currentEvidence?.fileName}</p>
                )}
              </div>

              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium mb-2">Share With</label>
                <select
                  value={selectedShareRecipient}
                  onChange={(e) => setSelectedShareRecipient(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
                >
                  <option value="">Select recipient...</option>
                  {caseParties?.filter((p: CaseParty) => p.Email !== user?.email).map((party: CaseParty) => (
                    <option key={party.Email} value={party.Email}>
                      {party.Name} ({party.Role})
                    </option>
                  ))}
                </select>
              </div>

              {/* Optional Message */}
              <div>
                <label className="block text-sm font-medium mb-2">Message (Optional)</label>
                <textarea
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  placeholder="Add a note for the recipient..."
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 h-20 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareSubmit}
                  disabled={!selectedShareRecipient || createShareMutation.isPending}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded font-medium flex items-center justify-center gap-2"
                >
                  {createShareMutation.isPending ? (
                    <>Sharing...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Share
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Received Items Panel */}
      {showReceivedPanel && !isFullscreen && (
        <div className="fixed top-16 right-4 w-80 bg-gray-800 rounded-lg shadow-2xl text-white z-40 max-h-96 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Received Items
            </h3>
            <button
              onClick={() => setShowReceivedPanel(false)}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1">
            {pendingShares.map((share: CourtShare) => (
              <div key={share.Id} className="p-3 border-b border-gray-700 hover:bg-gray-750">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {share.ExhibitNumber ? `${share.ExhibitNumber} - ${share.ExhibitTitle}` : share.EvidenceTitle}
                    </p>
                    <p className="text-xs text-gray-400">
                      From: {share.SharedByName} ({share.SharedByRole})
                    </p>
                  </div>
                </div>
                
                {share.Message && (
                  <p className="text-xs text-gray-300 mb-2 italic">"{share.Message}"</p>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => viewSharedItem(share)}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs font-medium"
                  >
                    <CheckCircle className="w-3 h-3" />
                    View & Acknowledge
                  </button>
                  <button
                    onClick={() => dismissMutation.mutate(share.Id)}
                    className="px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded"
                    title="Dismiss"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {pendingShares.length === 0 && (
              <div className="p-4 text-center text-gray-400 text-sm">
                No pending items to review
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
